/** Cold Outbound contacts: upload a CSV, add people by hand, or pick existing leads / merchants. */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Search, Upload, X } from 'lucide-react';
import api from '../../lib/api';
import { errMsg } from './Templates';

const inp = 'h-9 rounded-md border bg-background px-3 text-sm';
const SOURCES = [['csv', 'Upload CSV'], ['manual', 'Add by hand'], ['lead', 'Pick leads'], ['merchant', 'Pick merchants']];

function CsvSource({ onAdd }) {
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const r = await api.request('/communications/contacts/parse-csv', { method: 'POST', body: fd }); onAdd(r.contacts); toast.success(`${r.contacts.length} contacts added${r.skipped ? ` · ${r.skipped} skipped (no valid email or duplicate)` : ''}`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <label className="flex h-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed text-sm hover:bg-muted" data-testid="cold-csv-label">
      <Upload className="h-4 w-4" /><span>Choose a CSV with an <b>email</b> column (first name, last name, business, city, state optional)</span>
      <input type="file" accept=".csv" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} data-testid="cold-csv-input" />
    </label>
  );
}

function ManualSource({ onAdd }) {
  const [c, setC] = useState({ email: '', first_name: '', business_name: '' });
  const add = () => {
    if (!/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(c.email.trim())) { toast.error('Enter a valid email'); return; }
    onAdd([{ ...c, email: c.email.trim().toLowerCase() }]); setC({ email: '', first_name: '', business_name: '' });
  };
  const f = (k, ph) => <input className={inp} placeholder={ph} value={c[k]} onChange={(e) => setC({ ...c, [k]: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && add()} data-testid={`cold-manual-${k}`} />;
  return <div className="flex flex-wrap gap-2">{f('email', 'email@business.com')}{f('first_name', 'First name')}{f('business_name', 'Business')}<button type="button" className="h-9 rounded-full border px-4 text-sm" onClick={add} data-testid="cold-manual-add-btn">Add</button></div>;
}

function PickSource({ kind, onAdd }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const search = async () => { try { setResults((await api.request(`/communications/contacts/search?kind=${kind}&q=${encodeURIComponent(q)}`)).results); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-2">
      <div className="flex gap-2"><input className={`${inp} flex-1`} placeholder={`Search ${kind}s by name or email`} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()} data-testid={`cold-search-${kind}`} /><button type="button" className="inline-flex h-9 items-center gap-1 rounded-full border px-4 text-sm" onClick={search} data-testid={`cold-search-${kind}-btn`}><Search className="h-4 w-4" />Search</button></div>
      {results && (results.length ? <div className="max-h-56 divide-y overflow-y-auto rounded-lg border">{results.map((r) => (
        <button key={r.id} type="button" className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted" onClick={() => onAdd([{ ...r, [`${kind}_id`]: r.id }])} data-testid={`cold-pick-${r.id}`}>
          <span>{r.business_name || `${r.first_name || ''} ${r.last_name || ''}`}<span className="block text-xs text-muted-foreground">{r.email}</span></span><span className="text-xs text-primary">Add</span>
        </button>))}
        {results.length > 1 && <button type="button" className="w-full px-3 py-2 text-xs font-medium text-primary" onClick={() => onAdd(results.map((r) => ({ ...r, [`${kind}_id`]: r.id })))} data-testid={`cold-pick-all-${kind}`}>Add all {results.length}</button>}
      </div> : <p className="text-sm text-muted-foreground">No {kind}s with an email match.</p>)}
    </div>
  );
}

export function ContactPicker({ contacts, setContacts }) {
  const [src, setSrc] = useState('csv');
  const add = (rows) => {
    const seen = new Set(contacts.map((c) => c.email));
    setContacts([...contacts, ...rows.filter((r) => r.email && !seen.has(r.email) && seen.add(r.email))]);
  };
  return (
    <div className="space-y-3" data-testid="cold-contact-picker">
      <div className="flex flex-wrap gap-2">{SOURCES.map(([k, l]) => <button key={k} type="button" onClick={() => setSrc(k)} className={`rounded-full px-3 py-1 text-xs ${src === k ? 'bg-foreground text-background' : 'border'}`} data-testid={`cold-source-${k}`}>{l}</button>)}</div>
      {src === 'csv' && <CsvSource onAdd={add} />}
      {src === 'manual' && <ManualSource onAdd={add} />}
      {(src === 'lead' || src === 'merchant') && <PickSource key={src} kind={src} onAdd={add} />}
      <div className="flex flex-wrap gap-1.5" data-testid="cold-contact-list">
        {contacts.slice(0, 40).map((c) => <span key={c.email} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs">{c.email}<button type="button" aria-label={`Remove ${c.email}`} onClick={() => setContacts(contacts.filter((x) => x.email !== c.email))}><X className="h-3 w-3" /></button></span>)}
        {contacts.length > 40 && <span className="text-xs text-muted-foreground">+{contacts.length - 40} more</span>}
      </div>
      <p className="text-xs text-muted-foreground" data-testid="cold-contact-count">{contacts.length} contact{contacts.length === 1 ? '' : 's'} selected</p>
    </div>
  );
}
